import {
	AgrupamentoEspecial,
	DadosAgrupamento,
	Estacao,
	FormularioRelatorio,
	INPUTS_RELATORIOS,
	InstanciaRelatorio,
	Municipio,
	PostosRelatorios,
} from '../../../interfaces';
import { FormGroup, Validators } from '@angular/forms';
import { GroupButton } from '@componentes/public-button-group/public-button-group.component';
import { Select } from '@layout/interfaces/select';
import { RelatoriosService } from '../../../services';
import { ToastrService } from 'ngx-toastr';
import { RelatorioNDCResponse } from '../../../interfaces/relatorio-ndc';
import { HttpErrorResponse } from '@angular/common/http';
import * as pdfseira from '@utils/pdf-seira';
import { DocumentExporter } from '@utils/document-exporter';
import * as ExcelTable from 'mr-excel';
import { DateTimeUtils } from '@utils/datetime-util';
import { corrigeDuplicacaoNome } from '../../../utils';
import {
	alinharDado,
	baixarEAbrirPDF,
	enumAsSelectOptions,
	isNuloOuUndefined,
} from '@utils';
import {
	Component,
	OnDestroy,
	OnInit,
	ViewChild,
	inject,
	AfterViewInit,
	ChangeDetectorRef,
} from '@angular/core';
import { numberToBrNumberTables } from '@utils';
import { MapaNdcComponent } from '../mapa-ndc/mapa-ndc.component';
import { Subject, takeUntil } from 'rxjs';
import { NumberUtils } from '@utils/number-utils';

type MapCapture = {
	dataUrl: string;
	mapRect: { x: number; y: number; w: number; h: number };
	canvasW: number;
	canvasH: number;
};

@Component({
	selector: 'seira-pluviometro-mapa-ndc',
	templateUrl: './pluviometro-mapa-ndc.component.html',
	styleUrls: ['./pluviometro-mapa-ndc.component.scss'],
})
export class PluviometroMapaNdcComponent
	implements OnDestroy, InstanciaRelatorio, OnInit, AfterViewInit
{
	form?: FormGroup<any> | undefined;
	opcoesMunPostos?: Select<string>[] | undefined;
	variaveisMosaico?: Select<string>[] | undefined;
	mapa: MapaNdcComponent;
	@ViewChild(MapaNdcComponent, { static: false })
	set mapaInterpolacao(mapa: MapaNdcComponent) {
		if (mapa) {
			this.mapa = mapa;
		}
	}
	postos?: PostosRelatorios[] | undefined;
	estacoes?: Estacao[] | undefined;
	regioes?: Select<string>[] | undefined;
	microrregioes?: Select<string>[] | undefined;
	mesorregioes?: Select<string>[] | undefined;
	municipios?: Municipio[] | undefined;
	precipitacao?: Select<string>[] | undefined;
	agrupamento?: Select<string>[] | undefined;
	inputs = inject(INPUTS_RELATORIOS);
	private _isLoading = true;
	response: RelatorioNDCResponse[] = [];
	isLoadingInitial = true;
	agrupamentoSelecionado: string;
	periodoTitulo = '';
	descricaoRelatorio =
		'O NDC, Número de dias com chuva, representa o número de dias em que a pluviometria diária, acumulada em 24 horas, foi igual ou superior a 0,1 mm.';
	botoesDeExportacao: GroupButton[] = [
		{
			label: '.png',
			size: 'small',
			icon: 'ph-image',
			onClick: async () => {
				await this.exportarPNG();
			},
		},

		{
			label: '.pdf',
			size: 'small',
			icon: 'ph-file-pdf',
			activeFakeLoading: true,
			onClick: () => {
				if (this.mapa) {
					this.mapa.resetMapView();
				}
				setTimeout(() => {
					this.exportarPDF();
				}, 1000);
			},
		},
		{
			label: '.csv',
			size: 'small',
			icon: 'ph-file-csv',
			onClick: () => this.exportarCSV(),
		},
		{
			label: '.txt',
			size: 'small',
			icon: 'ph-file-text',
			onClick: () => this.exportarTXT(),
		},
		{
			label: '.xlsx',
			size: 'small',
			icon: 'ph-file-xls',
			onClick: () => this.exportarXLSX(),
		},
	];
	_destroyed = new Subject();
	constructor(
		private relatorioService: RelatoriosService,
		private toastr: ToastrService,
		private cdr: ChangeDetectorRef
	) {
		this.agrupamento = enumAsSelectOptions(AgrupamentoEspecial);
		this.setValidators();
	}
	ngOnInit(): void {
		this.inputs.form
			.get(FormularioRelatorio.AGRUPAMENTO)
			?.setValue(this.agrupamento![0].value);
		this.observarValores();
	}
	ngAfterViewInit(): void {
		this.cdr.detectChanges();
	}
	ngOnDestroy(): void {
		this.inputs.form.get(FormularioRelatorio.PERIODO_BUSCA)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.DATA_INICIO)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.DATA_FIM)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.AGRUPAMENTO)?.clearValidators();
		this._destroyed.next(undefined);
	}

	setValidators() {
		this.inputs.form
			.get(FormularioRelatorio.PERIODO_BUSCA)
			?.setValidators(Validators.required);
		this.inputs.form
			.get(FormularioRelatorio.DATA_INICIO)
			?.setValidators(Validators.required);
		this.inputs.form
			.get(FormularioRelatorio.AGRUPAMENTO)
			?.setValidators(Validators.required);
		this.inputs.form
			.get(FormularioRelatorio.DATA_FIM)
			?.setValidators(Validators.required);
	}

	get isLoading(): boolean {
		return this._isLoading;
	}

	set isLoading(value: boolean) {
		this._isLoading = value;
		this.inputs.setLoading(value);
	}

	gerarRelatorio() {
		const diaInicio = this.inputs.form.get(FormularioRelatorio.DATA_INICIO)
			?.value;
		const diaFim = this.inputs.form.get(FormularioRelatorio.DATA_FIM)?.value;
		const periodo = this.inputs.form.get(FormularioRelatorio.PERIODO_BUSCA)
			?.value;
		this.agrupamentoSelecionado = this.inputs.form.get(
			FormularioRelatorio.AGRUPAMENTO
		)?.value;

		if (!diaInicio || !diaFim || !periodo || this.inputs.form.invalid) {
			this.isLoadingInitial = false;
			this.isLoading = false;
			return;
		}

		this.periodoTitulo = DateTimeUtils.formatarDataPeriodo(
			diaInicio,
			diaFim,
			periodo
		);

		this.isLoading = true;

		const dados: DadosAgrupamento = {
			diaInicio,
			diaFim,
			agrupamento: this.agrupamentoSelecionado,
			periodoAgrupamento: periodo.toUpperCase(),
		};

		switch (this.agrupamentoSelecionado) {
			case 'MUNICIPIO_POSTO':
				dados.posto = 0;
				break;
			case 'MICRORREGIAO':
				dados.microrregiao = 0;
				break;
			case 'MUNICIPIO':
				dados.municipio = 0;
				break;
		}

		this.relatorioService.buscarRelatorioNDCPorAgrupamento(dados).subscribe({
			next: resp => {
				const todasPrecsSaoNull = resp.every(el =>
					isNuloOuUndefined(el.diasComChuva)
				);
				this.relatorioService.atualizaVisibilidadeBotoesExportacao(
					todasPrecsSaoNull ? false : resp
				);
				this.response = resp;
			},
			error: (erro: HttpErrorResponse) => {
				this.toastr.error('Nenhum resultado encontrado', erro.message);
				this.isLoadingInitial = false;
				this.isLoading = false;
			},
			complete: () => {
				this.isLoadingInitial = false;
				this.isLoading = false;
			},
		});
	}

	getDadosTabelaParaExportacao(dados: RelatorioNDCResponse[]) {
		const tableData: any[][] = [];
		if (this.agrupamentoSelecionado === 'MUNICIPIO_POSTO') {
			const colunas = [
				'Município/Posto',
				'Latitude',
				'Longitude',
				'Altitude',
				'Microrregião',
				'Mesorregião',
				'Região',
				'Bacia e Sub-Bacia',
				'Tipo',
				'Precipitação acumulada (mm)',
				'Dias com chuva',
			];
			tableData.push(colunas);
		} else {
			const colunas = [
				AgrupamentoEspecial[
					this.agrupamentoSelecionado as keyof typeof AgrupamentoEspecial
				],
				'Número de dias com chuva',
				'Precipitação acumulada (mm)',
			];
			tableData.push(colunas);
		}
		if (this.agrupamentoSelecionado === 'MUNICIPIO_POSTO') {
			dados.forEach((item: RelatorioNDCResponse) => {
				const rowData = [
					corrigeDuplicacaoNome(item.nome),
					numberToBrNumberTables(item.latitude, 4),
					numberToBrNumberTables(item.longitude, 4),
					numberToBrNumberTables(item.altitude, 1),
					item.microrregiao,
					item.mesorregiao,
					item.regiao,
					item.bacia !== null && item.bacia !== ''
						? item.bacia
						: item.subbacia !== null && item.subbacia !== ''
						? item.subbacia
						: '',
					this.getTipo(item),
					numberToBrNumberTables(item.somaPrecipitacao, 1),
					item.diasComChuva,
				];
				tableData.push(rowData);
			});
		} else {
			dados.forEach((item: RelatorioNDCResponse) => {
				const rowData = [
					item.nome === item.municipio + '/' + item.municipio
						? item.municipio
						: item.nome,
					item.diasComChuva,
					numberToBrNumberTables(item.somaPrecipitacao, 1),
				];
				tableData.push(rowData);
			});
		}

		return tableData;
	}

	async getMapImage() {
		return this.mapa?.getMapImage?.() ?? null;
	}

	private resolveAsset(path: string) {
		return new URL(path, document.baseURI).toString();
	}

	private carregarImagem(src: string): Promise<HTMLImageElement> {
		return new Promise((resolve, reject) => {
			const image = new Image();
			image.crossOrigin = 'anonymous';
			image.onload = () => resolve(image);
			image.onerror = err => reject(err);
			image.src = src;
		});
	}

	private async montarMapaComLogos(
		capture: MapCapture,
		opts?: { showHeader?: boolean; showPeriodo?: boolean }
	): Promise<string> {
		const showHeader = opts?.showHeader ?? true;
		const showPeriodo = opts?.showPeriodo ?? true;

		const logoGovernoSrc = this.resolveAsset(
			'assets/images/logos/governo_pb.png'
		);
		const logoAesaSrc = this.resolveAsset('assets/images/logos/logoAesa.svg');

		const results = await Promise.allSettled([
			this.carregarImagem(capture.dataUrl),
			this.carregarImagem(logoGovernoSrc),
			this.carregarImagem(logoAesaSrc),
		]);

		const mapImage =
			results[0].status === 'fulfilled' ? results[0].value : null;
		const logoGov = results[1].status === 'fulfilled' ? results[1].value : null;
		const logoAesa =
			results[2].status === 'fulfilled' ? results[2].value : null;

		if (!mapImage) return capture.dataUrl;

		const offsetY = 200;

		const canvas = document.createElement('canvas');
		canvas.width = mapImage.naturalWidth;
		canvas.height = mapImage.naturalHeight + offsetY;

		const ctx = canvas.getContext('2d')!;
		ctx.fillStyle = '#ffffff';
		ctx.fillRect(0, 0, canvas.width, canvas.height);

		ctx.drawImage(mapImage, 0, offsetY);
		const mapX0 = capture.mapRect.x;
		const mapY0 = offsetY + capture.mapRect.y;
		const mapX1 = mapX0 + capture.mapRect.w;

		const insidePad = 75;
		const maxLogoHInside = Math.round(Math.min(70, capture.mapRect.h * 0.12));

		const drawLogoInside = (imgEl: HTMLImageElement, x: number, y: number) => {
			const ratio = imgEl.naturalWidth / imgEl.naturalHeight;
			const h = maxLogoHInside;
			const w = Math.round(h * ratio);

			ctx.closePath();
			ctx.fill();
			ctx.stroke();
			ctx.restore();

			ctx.drawImage(imgEl, x, y, w, h);
		};

		const mapY1 = mapY0 + capture.mapRect.h;
		const hLogo = maxLogoHInside;
		const govDx = -105;
		const govDy = -30;
		const aesaDx = 91;
		const aesaDy = -30;

		if (logoGov) {
			const wG = Math.round(
				hLogo * (logoGov.naturalWidth / logoGov.naturalHeight)
			);
			drawLogoInside(
				logoGov,
				mapX1 - insidePad - wG + govDx,
				mapY1 - insidePad - hLogo + govDy
			);
		}

		if (logoAesa) {
			const wA = Math.round(
				hLogo * (logoAesa.naturalWidth / logoAesa.naturalHeight)
			);
			drawLogoInside(
				logoAesa,
				mapX0 + insidePad + aesaDx,
				mapY1 - insidePad - hLogo + aesaDy
			);
		}

		let headerH = 0;

		if (showHeader) {
			headerH = Math.max(52, Math.min(90, Math.round(canvas.height * 0.085)));

			ctx.save();
			ctx.fillStyle = 'rgba(255,255,255,0.92)';
			ctx.fillRect(0, 0, canvas.width, headerH);

			ctx.strokeStyle = 'rgba(17,24,39,0.12)';
			ctx.lineWidth = 1;
			ctx.beginPath();
			ctx.moveTo(0, headerH - 0.5);
			ctx.lineTo(canvas.width, headerH - 0.5);
			ctx.stroke();

			const titulo = 'Número de dias com chuva (NDC)';
			const periodo = this.periodoTitulo ?? '';

			ctx.textAlign = 'center';
			ctx.textBaseline = 'middle';

			const font1 = Math.max(24, Math.min(36, Math.round(headerH * 0.5)));
			const font2 = Math.max(24, Math.min(36, Math.round(headerH * 0.5)));

			ctx.fillStyle = '#111827';
			ctx.font = `700 ${font1}px Poppins, Arial, sans-serif`;
			ctx.fillText(titulo, canvas.width / 2, headerH * 1.8);

			if (showPeriodo && periodo.trim()) {
				ctx.font = `600 ${font2}px Poppins, Arial, sans-serif`;
				ctx.fillText(periodo, canvas.width / 2, headerH * 2.3);
			}

			ctx.restore();
		}

		return canvas.toDataURL('image/png', 1);
	}

	private async recortarDataUrlSides(
		dataUrl: string,
		rect: { x: number; y: number; w: number; h: number },
		pad: { top?: number; right?: number; bottom?: number; left?: number } = {}
	): Promise<string> {
		const img = await this.carregarImagem(dataUrl);

		const top = pad.top ?? 0;
		const right = pad.right ?? 0;
		const bottom = pad.bottom ?? 10;
		const left = pad.left ?? 0;

		const sx = Math.max(0, Math.floor(rect.x - left));
		const sy = Math.max(0, Math.floor(rect.y - top));
		const sw = Math.min(
			img.naturalWidth - sx,
			Math.floor(rect.w + left + right)
		);
		const sh = Math.min(
			img.naturalHeight - sy,
			Math.floor(rect.h + top + bottom)
		);

		const out = document.createElement('canvas');
		out.width = sw;
		out.height = sh;

		const ctx = out.getContext('2d')!;
		ctx.drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh);

		return out.toDataURL('image/png', 1);
	}

	private get nomeExportacaoBase(): string {
		return `N\u00famero de dias com chuva (NDC) - ${this.periodoTitulo}`;
	}

	async exportarPDF() {
		if (this.mapa?.resetMapView) {
			this.mapa.resetMapView();
		}

		await new Promise(resolve => setTimeout(resolve, 750));
		this.esconderElementosDaExportacao();
		try {
			await new Promise(resolve => setTimeout(resolve, 250));

			const documentDefinition: any = await pdfseira.documentDefinitions();
			const capture = await this.getMapImage();
			if (!capture) {
				console.error('Não foi possível capturar a imagem do mapa.');
				return;
			}
			let imagemComCabecalho = capture.dataUrl;
			try {
				imagemComCabecalho = await this.montarMapaComLogos(capture, {
					showHeader: false,
					showPeriodo: false,
				});
			} catch (e) {
				console.error('[Export PDF] Erro ao montar imagem do mapa:', e);
			}

			documentDefinition.content.push([
				{
					text: `Número de dias com chuva (NDC) - ${this.periodoTitulo}`,
					alignment: 'center',
					margin: [0, 6, 0, 6],
				},
				{
					image: imagemComCabecalho,
					fit: [500, 400],
					alignment: 'center',
					margin: [0, -8, 0, 0],
				},
			]);

			documentDefinition.content.push({
				table: {
					widths: ['33,33%', '33,33%', '33,33%'],
					body: this.tableData(),
					layout: {
						noWrap: false,
						fontSize: 5,
					},
				},
				marginLeft: 15,
				marginRight: 15,
			});

			baixarEAbrirPDF(
				`Número de dias com chuva (NDC) - ${this.periodoTitulo}`,
				documentDefinition
			);
		} finally {
			this.mostrarElementosDaExportacao();
		}
	}

	exportarCSV() {
		const tableData = this.getDadosTabelaParaExportacao(this.response);

		DocumentExporter.gerarCSV(
			tableData,
			`Número de dias com chuva (NDC) - ${this.periodoTitulo}`
		);
	}

	exportarTXT() {
		const tableData = this.getDadosTabelaParaExportacao(this.response);
		let txtData = '';
		if (tableData.length > 0) {
			const colunas = tableData[0];

			tableData.slice(1).forEach(dadoRelatorio => {
				colunas.forEach((coluna: string, index: number) => {
					const valor = dadoRelatorio[index];
					txtData += `${coluna}: ${valor ?? '-'}\n`;
				});

				txtData += '\n';
			});
		}

		DocumentExporter.gerarTXT(
			txtData,
			`Número de dias com chuva (NDC) - ${this.periodoTitulo}`
		);
	}

	exportarXLSX() {
		const nomeArquivo = `Número de dias com chuva (NDC) - ${this.periodoTitulo}`;
		const agrupamentoAtual = this.agrupamentoSelecionado;

		const colorPalette = {
			c1: '2C3639',
			c2: 'FFFFFF',
			c3: '000000',
			c4: 'EEEEEE',
		};

		const rowStyle = {
			backgroundColor: colorPalette.c2,
			color: colorPalette.c3,
		};
		const headerStyle = {
			backgroundColor: colorPalette.c4,
			color: colorPalette.c1,
			bold: true,
		};
		const headerStyleCenter = {
			backgroundColor: colorPalette.c4,
			color: colorPalette.c1,
			bold: true,
			alignment: {
				horizontal: 'center',
				vertical: 'center',
			} as ExcelTable.DataModel.AlignmentOption,
		};
		const rowStyleCenter = {
			backgroundColor: colorPalette.c2,
			color: colorPalette.c3,
			alignment: {
				horizontal: 'center',
				vertical: 'center',
			} as ExcelTable.DataModel.AlignmentOption,
		};

		const title = {
			backgroundColor: colorPalette.c2,
			whiteSpace: 'pre',
			color: colorPalette.c3,
			bold: true,
			alignment: {
				horizontal: 'center',
				vertical: 'center',
				wrapText: 1,
			} as ExcelTable.DataModel.AlignmentOption,
		};

		const headers = this.getHeaders();
		const dataExcel = {
			styles: {
				headerStyle,
				headerStyleCenter,
				rowStyle,
				rowStyleCenter,
				title,
			},
			sheet: [
				{
					shiftTop: 3,
					images: [
						{
							url: 'assets/images/cabecalho/cabeçalho_excel.png',
							from: 'A1',
							to: 'C3',
						},
					],
					styleCellCondition(
						data: any,
						object: any,
						rowIndex: number,
						colIndex: number
					) {
						if (data && data.label) {
							return 'headerStyleCenter';
						} else {
							if (colIndex === 0 && agrupamentoAtual === 'MUNICIPIO_POSTO') {
								return 'rowStyle';
							} else {
								return 'rowStyleCenter';
							}
						}
					},
					headers: headers,
					data: this.response.map(ndc => {
						return {
							diasComChuva: ndc.diasComChuva,
							somaPrecipitacao: isNuloOuUndefined(ndc.somaPrecipitacao)
								? '-'
								: NumberUtils.getIgnorandoCasasDecimais(ndc.somaPrecipitacao),
							nome: corrigeDuplicacaoNome(ndc.nome),
							latitude: isNuloOuUndefined(ndc.latitude)
								? '-'
								: NumberUtils.getIgnorandoCasasDecimais(ndc.latitude!, 4),
							longitude: isNuloOuUndefined(ndc.longitude)
								? '-'
								: NumberUtils.getIgnorandoCasasDecimais(ndc.longitude!, 4),
							altitude: isNuloOuUndefined(ndc.altitude)
								? '-'
								: NumberUtils.getIgnorandoCasasDecimais(ndc.altitude!),
							microrregiao: ndc.microrregiao,
							mesorregiao: ndc.mesorregiao,
							regiao: ndc.regiao,
							bacia:
								ndc.bacia !== null && ndc.bacia !== ''
									? ndc.bacia
									: ndc.subbacia !== null && ndc.subbacia !== ''
									? ndc.subbacia
									: '-',
							tipo: this.getTipo(ndc),
						};
					}),
					columns: [
						{ key: 'nome' },
						{
							key: 'latitude',
							style: { numFmt: '0.0' },
						},
						{
							key: 'longitude',
							style: { numFmt: '0.0' },
						},
						{
							key: 'altitude',
							style: { numFmt: '0.0' },
						},
						{
							key: 'microrregiao',
						},
						{
							key: 'mesorregiao',
						},
						{
							key: 'regiao',
						},
						{
							key: 'diasComChuva',
							style: { numFmt: '0' },
						},
						{
							key: 'Pluviometria observada (mm)',
							style: { numFmt: '0' },
						},
					],
					title: {
						consommeRow: 3,
						consommeCol: 3,
						text: `${nomeArquivo}`,
						styleId: 'title',
					},
				},
			],
			fileName: nomeArquivo,
		};

		ExcelTable.generateExcel(dataExcel);
	}

	private tableData() {
		const dadosTabela: Array<any[]> = [];
		let valores;
		const cabecalho = [
			{
				text: AgrupamentoEspecial[
					this.agrupamentoSelecionado as keyof typeof AgrupamentoEspecial
				],
				fillColor: '#DCDCDC',
				alignment: 'center',
			},
			{
				text: 'Número de dias com chuva',
				fillColor: '#DCDCDC',
				alignment: 'center',
			},
			{
				text: 'Pluviometria observada (mm)',
				fillColor: '#DCDCDC',
				alignment: 'center',
			},
		];
		dadosTabela.push(cabecalho);

		if (this.agrupamentoSelecionado === 'MUNICIPIO_POSTO') {
			valores = this.response?.map(value => [
				corrigeDuplicacaoNome(value.nome),
				alinharDado(value.diasComChuva, true),
				alinharDado(numberToBrNumberTables(value.somaPrecipitacao, 1), true),
			]);
		} else {
			valores = this.response?.map(value => [
				alinharDado(value.nome, true),
				alinharDado(value.diasComChuva, true),
				alinharDado(numberToBrNumberTables(value.somaPrecipitacao, 1), true),
			]);
		}

		dadosTabela.push(...valores);

		return dadosTabela;
	}
	getHeaders() {
		const headers: Array<{ label: string; text: string; size: number }> = [];
		if (this.agrupamentoSelecionado === 'MUNICIPIO_POSTO') {
			headers.push(
				{
					label: 'nome',
					text: AgrupamentoEspecial[
						this.agrupamentoSelecionado as keyof typeof AgrupamentoEspecial
					],
					size: 24,
				},
				{ label: 'latitude', text: 'Latitude', size: 24 },
				{ label: 'longitude', text: 'Longitude', size: 24 },
				{ label: 'altitude', text: 'Altitude', size: 24 },
				{ label: 'microrregiao', text: 'Microrregião', size: 24 },
				{ label: 'mesorregiao', text: 'Mesorregiao', size: 24 },
				{ label: 'regiao', text: 'Região', size: 24 },
				{ label: 'bacia', text: 'Bacia/Sub-bacia', size: 24 },
				{ label: 'tipo', text: 'Tipo', size: 24 },
				{ label: 'diasComChuva', text: 'Dias com chuva', size: 24 },
				{
					label: 'somaPrecipitacao',
					text: 'Pluviometria  observada (mm)',
					size: 24,
				}
			);
		} else {
			headers.push(
				{
					label: 'nome',
					text: AgrupamentoEspecial[
						this.agrupamentoSelecionado as keyof typeof AgrupamentoEspecial
					],
					size: 48,
				},
				{ label: 'diasComChuva', text: 'Dias com chuva', size: 48 },
				{
					label: 'somaPrecipitacao',
					text: 'Pluviometria observada (mm)',
					size: 48,
				}
			);
		}
		return headers;
	}
	getTipo(item: RelatorioNDCResponse) {
		if (item.bacia !== null && item.bacia?.includes('Região')) {
			return 'Região de Curso';
		} else if (
			item.bacia !== null &&
			item.bacia !== '' &&
			!item.bacia?.includes('Região')
		) {
			return 'Bacia';
		} else if (item.subbacia !== null) {
			return 'Sub-Bacia';
		} else {
			return '-';
		}
	}
	observarValores() {
		this.inputs.form
			.get(FormularioRelatorio.PERIODO_BUSCA)
			?.valueChanges.pipe(takeUntil(this._destroyed))
			.subscribe({
				next: value => {
					const hoje = new Date();
					if (value.toUpperCase() === 'ANUAL') {
						this.inputs.form.patchValue({
							dataInicio: hoje,
							dataFim: hoje,
						});
					}
				},
			});

		this.inputs.form
			.get(FormularioRelatorio.DATA_INICIO)
			?.valueChanges.pipe(takeUntil(this._destroyed))
			.subscribe({
				next: value => {
					if (this.isPeriodoAnualEAnosDiferentes()) {
						this.inputs.form.patchValue({ dataFim: value });
					}
				},
			});

		this.inputs.form
			.get(FormularioRelatorio.DATA_FIM)
			?.valueChanges.pipe(takeUntil(this._destroyed))
			.subscribe({
				next: value => {
					if (this.isPeriodoAnualEAnosDiferentes()) {
						this.inputs.form.patchValue({ dataInicio: value });
					}
				},
			});
	}

	isPeriodoAnualEAnosDiferentes() {
		const isPeriodoAnual =
			this.getFormItemValue(FormularioRelatorio.PERIODO_BUSCA).toUpperCase() ===
			'ANUAL';

		const dataInicio = this.getFormItemValue(FormularioRelatorio.DATA_INICIO);
		const dataFim = this.getFormItemValue(FormularioRelatorio.DATA_FIM);
		const isAnosDiferentes =
			new Date(dataInicio).getFullYear() !== new Date(dataFim).getFullYear();

		return isPeriodoAnual && isAnosDiferentes;
	}
	getFormItemValue(formValue: string) {
		return this.inputs.form.get(formValue)?.value;
	}

	private downloadDataUrl(nomeArquivoSemExt: string, dataUrl: string) {
		const a = document.createElement('a');
		a.href = dataUrl;
		a.download = `${nomeArquivoSemExt}.png`;
		a.style.display = 'none';
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
	}

	async exportarPNG() {
		try {
			if (this.mapa?.resetMapView) {
				this.mapa.resetMapView();
				await new Promise(r => setTimeout(r, 750));
			}

			this.esconderElementosDaExportacao();
			await new Promise(r => setTimeout(r, 200));

			const capture = await this.getMapImage();
			if (!capture) {
				this.toastr.error('Não foi possível capturar o mapa para PNG.');
				return;
			}

			try {
				const dataUrlFinal = await this.montarMapaComLogos(capture);
				const offsetY = 200;
				const recortado = await this.recortarDataUrlSides(
					dataUrlFinal,
					{
						x: capture.mapRect.x,
						y: offsetY + capture.mapRect.y,
						w: capture.mapRect.w,
						h: capture.mapRect.h,
					},
					{ bottom: 220, top: 80, left: -50, right: -90 }
				);

				this.downloadDataUrl(this.nomeExportacaoBase, recortado);
				return;
			} catch (e) {
				console.error('[Export PNG] Erro ao recortar:', e);
				this.downloadDataUrl(this.nomeExportacaoBase, capture.dataUrl);
				return;
			}

			/* const carregarImagem = (src: string): Promise<HTMLImageElement> => {
				return new Promise((resolve, reject) => {
					const img = new Image();
					img.crossOrigin = 'anonymous';
					img.onload = () => resolve(img);
					img.onerror = err => reject(err);
					img.src = src;
				});
			};

			const mapaImg = await carregarImagem(dataUrl!);

			const canvas = document.createElement('canvas');
			canvas.width = mapaImg.width;
			canvas.height = mapaImg.height;
			const ctx = canvas.getContext('2d')!;
			ctx.fillStyle = '#FFFFFF';
			ctx.fillRect(0, 0, canvas.width, canvas.height);
			ctx.drawImage(mapaImg, 0, 0, canvas.width, canvas.height);

			try {
				const logoAesa = await carregarImagem('assets/images/logos/aesa.png');

				const margem = canvas.width * 0.01;
				const larguraLogo = canvas.width * 0.05;
				const alturaLogo = (larguraLogo / logoAesa.width) * logoAesa.height;

				const xLogo = canvas.width - larguraLogo - margem;
				const yLogo = canvas.height - alturaLogo - margem;

				ctx.drawImage(logoAesa, xLogo, yLogo, larguraLogo, alturaLogo);
			} catch (e) {
				console.error('[Export PNG] Erro ao carregar logo da AESA:', e);
			}

			const finalDataUrl = canvas.toDataURL('image/png', 1);

			this.downloadDataUrl(
				`Número de dias com chuva (NDC) - ${this.periodoTitulo}`,
				finalDataUrl
			);
		*/
		} catch (e) {
			console.error(e);
			this.toastr.error('Falha ao exportar PNG.');
		} finally {
			this.mostrarElementosDaExportacao();
		}
	}

	private esconderElementosDaExportacao(): void {
		const seletores = [
			'.leaflet-control-container',
			'.leaflet-top',
			'.leaflet-bottom',
		];

		seletores.forEach(seletor => {
			document.querySelectorAll(seletor).forEach((el: Element) => {
				(el as HTMLElement).style.display = 'none';
			});
		});
	}

	private mostrarElementosDaExportacao(): void {
		const seletores = [
			'.leaflet-control-container',
			'.leaflet-top',
			'.leaflet-bottom',
		];

		seletores.forEach(seletor => {
			document.querySelectorAll(seletor).forEach((el: Element) => {
				(el as HTMLElement).style.display = '';
			});
		});
	}
}
